import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import logger from '../recording/logger';
import { captureFailed, recordingComplete } from './actions';
import { fetchVoicemailMessage, getVideoMailGreetingDraft } from './functions';
import { TAKE_IDLE, VIDEOMAIL_RECORDER_ASSIGN_WAIT_MS, VIDEOMAIL_SAVE_POLL_MS, VIDEOMAIL_SAVE_WAIT_MS, advanceTake } from './takeProgress';
/**
 * Reads the take back from the server once it has been saved. Returns its
 * length, or undefined while it is not ready yet.
 *
 * @param {string} messageId - The greeting or message ID.
 * @param {boolean} isGreeting - Whether it is a greeting.
 * @returns {Promise<number|undefined>}
 */
async function readSavedTake(messageId, isGreeting) {
    if (isGreeting) {
        const { greeting } = await getVideoMailGreetingDraft(messageId);
        return greeting?.status === 'draft' ? greeting.durationSeconds ?? 0 : undefined;
    }
    const message = await fetchVoicemailMessage(messageId);
    return message?.status === 'draft' ? message.duration_seconds ?? 0 : undefined;
}
/**
 * Follows a take the server-side recorder captures, on both the native
 * overlay and the web recorder:
 * - a press the recorder never answers fails after a while instead of
 * leaving the control on "Starting recording";
 * - a captured take that has ended shows as being saved, and the server is
 * asked whether it is ready to review, so a lost "complete" message does not
 * strand it;
 * - saving that never ends is reported, so the person can record again or
 * discard.
 *
 * @param {ITakeInput} input - The take and the recorder session in the room.
 * @returns {ITakeProgressState}
 */
export function useVideoMailTakeProgress({ capturing, isGreeting, messageId, roomName, sessionId, waitingForRecorder }) {
    const dispatch = useDispatch();
    const [progress, setProgress] = useState(TAKE_IDLE);
    const [saveTimedOut, setSaveTimedOut] = useState(false);
    const saving = progress.stage === 'saving';
    // Only before any recorder session of this take has shown up: a take
    // that is being saved has no session either, and is not waiting.
    const waiting = waitingForRecorder && progress.stage === 'idle';
    useEffect(() => {
        setProgress(TAKE_IDLE);
        setSaveTimedOut(false);
    }, [messageId, roomName]);
    useEffect(() => {
        setProgress(previous => advanceTake(previous, { capturing, sessionId }));
    }, [capturing, messageId, roomName, sessionId]);
    useEffect(() => {
        if (!waiting || !messageId) {
            return undefined;
        }
        const timeout = setTimeout(() => {
            logger.warn('VideoMail recorder was not assigned');
            dispatch(captureFailed(messageId));
        }, VIDEOMAIL_RECORDER_ASSIGN_WAIT_MS);
        return () => clearTimeout(timeout);
    }, [dispatch, messageId, waiting]);
    useEffect(() => {
        if (!saving || !messageId) {
            return undefined;
        }
        let active = true;
        let reading = false;
        const read = () => {
            if (reading) {
                return;
            }
            reading = true;
            readSavedTake(messageId, isGreeting)
                .then(durationSeconds => {
                if (active && durationSeconds !== undefined) {
                    dispatch(recordingComplete(messageId, durationSeconds, isGreeting ? 'greeting' : 'message'));
                }
            })
                .catch(() => undefined)
                .then(() => {
                reading = false;
            });
        };
        const poll = setInterval(read, VIDEOMAIL_SAVE_POLL_MS);
        const deadline = setTimeout(() => {
            clearInterval(poll);
            if (active) {
                logger.warn('VideoMail take was not saved in time');
                setSaveTimedOut(true);
            }
        }, VIDEOMAIL_SAVE_WAIT_MS);
        return () => {
            active = false;
            clearInterval(poll);
            clearTimeout(deadline);
        };
    }, [dispatch, isGreeting, messageId, saving]);
    return { saveTimedOut, saving };
}
